import React, {useMemo} from 'react';
import {Button, Input, Spin, Tree, Typography} from 'antd';
import {PlusCircleOutlined} from '@ant-design/icons';
import type {DataNode} from 'antd/es/tree';
import {useProjectCatalog} from '@/hooks/diagram/useProjectCatalog';
import type {CatalogElementHit, CatalogPackageNode, CatalogPlaceHint} from '@/types/api';

export type DiagramCatalogBrowserProps = {
	projectId: number;
	diagramId: number;
	onPlace: (identifier: string, hint: CatalogPlaceHint) => void;
	onNavigate?: (identifier: string) => void;
};

function packagesToTree(nodes: CatalogPackageNode[]): DataNode[] {
	return nodes.map(n => ({
		key: n.identifier,
		title: n.label || n.identifier,
		children: n.children?.length ? packagesToTree(n.children) : undefined,
	}));
}

const DiagramCatalogBrowser: React.FC<DiagramCatalogBrowserProps> = ({
	projectId,
	diagramId,
	onPlace,
	onNavigate,
}) => {
	const {query, setQuery, packageId, setPackageId, items, packages, loading} = useProjectCatalog({
		projectId,
		diagramId,
	});

	const treeData = useMemo(() => packagesToTree(packages), [packages]);
	const hasFilter = Boolean(query.trim() || packageId);

	return (
		<div style={{borderTop: '1px solid #f0f0f0', padding: '8px 8px 4px'}}>
			<Typography.Text type="secondary" style={{fontSize: 11, display: 'block', marginBottom: 6}}>
				Каталог проекта
			</Typography.Text>
			<Input.Search
				allowClear
				size="small"
				placeholder="Имя или identifier"
				value={query}
				onChange={e => setQuery(e.target.value)}
			/>
			{treeData.length > 0 && (
				<Tree
					blockNode
					selectable
					selectedKeys={packageId ? [packageId] : []}
					treeData={treeData}
					onSelect={keys => {
						const key = keys[0];
						setPackageId(key != null ? String(key) : null);
					}}
					style={{fontSize: 12, marginTop: 6, maxHeight: 90, overflowY: 'auto'}}
				/>
			)}
			<div style={{minHeight: 8, marginTop: 4}}>
				{loading ? (
					<div style={{textAlign: 'center', padding: 8}}>
						<Spin size="small" />
					</div>
				) : hasFilter && items.length === 0 ? (
					<Typography.Text type="secondary" style={{fontSize: 12}}>
						Ничего не найдено
					</Typography.Text>
				) : (
					items.map(item => (
						<CatalogHitRow
							key={item.identifier}
							item={item}
							onPlace={onPlace}
							onNavigate={onNavigate}
						/>
					))
				)}
			</div>
		</div>
	);
};

function CatalogHitRow({
	item,
	onPlace,
	onNavigate,
}: {
	item: CatalogElementHit;
	onPlace: DiagramCatalogBrowserProps['onPlace'];
	onNavigate?: DiagramCatalogBrowserProps['onNavigate'];
}) {
	const label = item.name?.trim() || item.type || item.identifier;
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 4,
				padding: '2px 0',
				opacity: item.onDiagram ? 1 : 0.85,
			}}
		>
			<button
				type="button"
				onClick={() => {
					if (item.onDiagram) onNavigate?.(item.identifier);
					else onPlace(item.identifier, {type: item.type ?? undefined, name: item.name ?? undefined});
				}}
				onDoubleClick={() => {
					if (!item.onDiagram) {
						onPlace(item.identifier, {type: item.type ?? undefined, name: item.name ?? undefined});
					}
				}}
				title={item.identifier}
				style={{
					flex: 1,
					minWidth: 0,
					textAlign: 'left',
					background: 'none',
					border: 'none',
					padding: '2px 0',
					cursor: 'pointer',
					fontSize: 12,
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				}}
			>
				{label}
				{item.type ? (
					<Typography.Text type="secondary" style={{marginLeft: 6, fontSize: 11}}>
						{item.type}
					</Typography.Text>
				) : null}
			</button>
			{!item.onDiagram && (
				<Button
					type="text"
					size="small"
					icon={<PlusCircleOutlined />}
					title="Добавить на диаграмму"
					onClick={() =>
						onPlace(item.identifier, {type: item.type ?? undefined, name: item.name ?? undefined})
					}
				/>
			)}
		</div>
	);
}

export default DiagramCatalogBrowser;
